<template>
  <div
    class="w-full border border-black/8 shdw flex items-center justify-between px-4 py-3 rounded-xl relative font-secondary text-gray-800 gap-x-9 cursor-pointer"
    :class="classes"
    @click="handleClick"
  >
    <div
      class="w-fit h-5 bg-red-500 rounded-tr-[10px] rounded-bl-[10px] text-white font-secondary text-[11px] leading-[12px] px-2.5 flex items-center justify-center absolute top-0 right-0 font-medium uppercase"
    >
      Limited time &bull; Save {{ DISCOUNT_PERCENTAGE }}%
    </div>

    <div>
      <div class="text-[14px] leading-[20px]">{{ plan.name }}</div>

      <div class="mb-1">
        <div
          v-if="isPAYG"
          class="flex flex-col text-[13px] leading-[20px] font-semibold"
        >
          <div>
            <span class="text-red-500 line-through">
              {{ originalPAYGPrice }}
            </span>
            {{ paygPrices.copyright?.details?.renderedPrice }}
            {{ paygPrices.copyright?.details?.suffix }}
          </div>
          <div class="text-[11px] leading-[18px]">
            + {{ paygPrices.subscription?.details?.renderedPrice }}
            {{ paygPrices.subscription?.details?.suffix }}
          </div>
        </div>

        <div v-else>
          <div class="text-[13px] font-semibold mb-[5px]">
            <span class="text-red-500 line-through">
              {{ originalBundlePrice }}
            </span>
            {{ lowestActivePlanItem?.prices[0].details?.renderedPrice }}
            {{ lowestActivePlanItem?.prices[0].details?.suffix }}
          </div>

          <Pill v-if="plan.priceCallout" class="slant" success>
            {{ plan.priceCallout }}
          </Pill>
        </div>
      </div>

      <div class="text-[13px] leading-[20px]">{{ plan.description }}</div>
    </div>

    <Checkbox v-model="selected" @click.stop />
  </div>
</template>

<script setup lang="ts">
import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { formatPrice } from '@/shared/utils/money'
import { Checkbox, Pill } from '@dacr/ui'
import { computed } from 'vue'

const DISCOUNT_PERCENTAGE = 46

const props = defineProps<{
  modelValue?: Plan
  plan: Plan
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', plan: Plan): void
}>()

const selected = computed({
  get() {
    return props.modelValue?.id === props.plan.id
  },
  set() {
    handleClick()
  },
})

const classes = computed(() => ({
  'border-green-500 bg-green-200/20 outline outline-green-500': selected.value,
  'hover:bg-gray-200/13': !selected.value,
}))

const isPAYG = computed(() => props.plan.items.length === 1)

const paygPrices = computed(() => {
  const subscription = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  const copyright = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.File
  )
  return { subscription, copyright }
})

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const originalPAYGPrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.File
  )
  const amount = (price?.details?.amount || 0) / 100
  return formatPrice({
    amount: Math.round(amount / (1 - DISCOUNT_PERCENTAGE / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const originalBundlePrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  const amount = (price?.details?.amount || 0) / 100
  return formatPrice({
    amount: Math.round(amount / (1 - DISCOUNT_PERCENTAGE / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const handleClick = () => {
  emit('update:modelValue', props.plan)
}
</script>

<style scoped>
.shdw {
  box-shadow:
    0 4px 18px 0 rgba(0, 0, 0, 0.04),
    0 0.8px 2.93px 0 rgba(0, 0, 0, 0.02);
}
</style>
